import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { useContactActions } from '@/hooks/useContactActions';
import { Contact } from '@/types/contact';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface ExportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContacts?: Contact[];
  onExportComplete?: () => void;
}

export const ExportContactsModal = ({
  isOpen,
  onClose,
  selectedContacts = [],
  onExportComplete,
}: ExportContactsModalProps) => {
  const { t } = useTranslation(['common', 'listContacts']);
  const {
    exportFields,
    handleExportFieldChange,
    exportContacts,
    prepareForExport,
    contactsToExport,
  } = useContactActions();

  // Préparer les contacts pour l'export quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && selectedContacts.length > 0) {
      prepareForExport(selectedContacts);
    }
  }, [isOpen, selectedContacts, prepareForExport]);

  const handleExport = () => {
    const success = exportContacts();
    if (success) {
      // Appeler le callback si l'exportation a réussi
      if (onExportComplete) {
        onExportComplete();
      }
      onClose();
    }
  };

  // Utiliser la valeur la plus précise disponible pour le nombre de contacts
  const displayCount =
    selectedContacts.length > 0
      ? selectedContacts.length
      : contactsToExport.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('listContacts:export.title', { count: displayCount })}
      className="w-auto"
    >
      <div className="py-4">
        <Text className="mb-4 text-sm text-gray-500">
          {t('listContacts:export.description')}
        </Text>

        <div className="mt-4 space-y-3">
          <Text className="font-medium">
            {t('listContacts:export.selectFields')}
          </Text>
          {exportFields.map(field => (
            <div
              key={field.field as string}
              className="flex items-center space-x-2"
            >
              <input
                type="checkbox"
                id={`field-${field.field}`}
                checked={field.checked}
                onChange={() => handleExportFieldChange(field)}
                className="size-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
              />
              <label
                htmlFor={`field-${field.field}`}
                className="cursor-pointer text-sm font-normal"
              >
                {field.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-col space-y-3 sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
        <Button onClick={onClose}>{t('common:cancel')}</Button>
        <Button
          className="bg-sky-600"
          onClick={handleExport}
          disabled={displayCount === 0}
        >
          {t('listContacts:export.confirm')}
        </Button>
      </div>
    </Modal>
  );
};
